// src/lib/types/workflow.ts
// Canonical workflow state machine. All dashboard view logic keys off WorkflowStatus.
// Phase 4: backend mirrors BackendWorkflowStatus; toFrontendStatus maps to these values.

export enum WorkflowStatus {
  PROCESSING       = 'PROCESSING',
  PENDING_REVIEW   = 'PENDING_REVIEW',
  STOCK_RESERVATION = 'STOCK_RESERVATION',
  EDIT_MODE        = 'EDIT_MODE',
  FINALIZE         = 'FINALIZE',
  APPROVED         = 'APPROVED',
  SENT             = 'SENT',
  REJECTED         = 'REJECTED',
}

export enum BackendWorkflowStatus {
  RECEIVED                = 'RECEIVED',
  PROCESSING              = 'PROCESSING',
  DRAFTED                 = 'DRAFTED',
  PENDING_APPROVAL        = 'PENDING_APPROVAL',
  APPROVED                = 'APPROVED',
  RESERVATION_CONFIRMED   = 'RESERVATION_CONFIRMED',
  SENT                    = 'SENT',
  REJECTED                = 'REJECTED',
  MANUAL_REVIEW_REQUIRED  = 'MANUAL_REVIEW_REQUIRED',
  FAILED                  = 'FAILED',
}

export function toFrontendStatus(backend: BackendWorkflowStatus): WorkflowStatus {
  switch (backend) {
    case BackendWorkflowStatus.RECEIVED:               return WorkflowStatus.PROCESSING;
    case BackendWorkflowStatus.PROCESSING:             return WorkflowStatus.PROCESSING;
    case BackendWorkflowStatus.DRAFTED:                return WorkflowStatus.PENDING_REVIEW;
    case BackendWorkflowStatus.PENDING_APPROVAL:       return WorkflowStatus.PENDING_REVIEW;
    case BackendWorkflowStatus.APPROVED:               return WorkflowStatus.STOCK_RESERVATION;
    case BackendWorkflowStatus.RESERVATION_CONFIRMED:  return WorkflowStatus.FINALIZE;
    case BackendWorkflowStatus.SENT:                   return WorkflowStatus.SENT;
    case BackendWorkflowStatus.REJECTED:               return WorkflowStatus.REJECTED;
    case BackendWorkflowStatus.MANUAL_REVIEW_REQUIRED: return WorkflowStatus.PENDING_REVIEW;
    case BackendWorkflowStatus.FAILED:                 return WorkflowStatus.PROCESSING;
  }
}

/**
 * Single source of truth for "this workflow still belongs in the
 * operator's active dashboard queue" — i.e. everything short of a
 * terminal state (SENT/REJECTED/QUARANTINED/MANUAL_REVIEW_REQUIRED isn't
 * currently surfaced anywhere and is deliberately left out of this list
 * too, pending its own UI).
 *
 * This used to be two independently-hardcoded lists that quietly drifted
 * apart: bookings.ts's ACTIVE_QUEUE_STATUSES (what gets fetched from the
 * backend — RECEIVED/PROCESSING/PENDING_APPROVAL only) vs.
 * DashboardPage.tsx's INBOX_QUEUE_STATUSES (what the InboxQueue is
 * actually built to render — which already included DRAFTED/APPROVED/
 * RESERVATION_CONFIRMED/FAILED, complete with badges for each). Because
 * the fetch-side list never grew to match, an APPROVED-but-not-yet-SENT
 * booking (e.g. sitting at the Stock Reservation screen awaiting a
 * manual Current RMS hold) was never fetched at all — so navigating away
 * and back (which remounts the dashboard's booking-queue state) made it
 * vanish from the operator's view entirely, even though it was still
 * genuinely in flight and showed correctly in the Audit Log. Reported
 * live 16 Sep 2026. See
 * claude/finding-approved-booking-vanishes-on-navigation-away.md.
 *
 * Both bookings.ts and DashboardPage.tsx now import this one constant
 * instead of maintaining their own copies, so they can't drift apart
 * again.
 */
export const ACTIVE_WORKFLOW_STATUSES = [
  'RECEIVED',
  'PROCESSING',
  'DRAFTED',
  'PENDING_APPROVAL',
  'APPROVED',
  'RESERVATION_CONFIRMED',
  'FAILED',
] as const;

export interface PaginatedWorkflowResponse {
  items: BackendWorkflow[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export type BackendWorkflow = {
  id: string;
  raw_email_id: string | null;
  status: BackendWorkflowStatus;
  assigned_to_id: string | null;
  assigned_to_email?: string | null;
  approved_by_id: string | null;
  approved_by_email?: string | null;
  rejected_by_id: string | null;
  status_reason: string;
  original_message_id: string | null;
  created_at: string;
  updated_at: string;
  available_transitions: BackendWorkflowStatus[];
  approved_at: string | null;
  rejected_at: string | null;
  sent_at: string | null;
  sender_email?: string | null;
  subject?: string | null;
  hold_expires_at?: string | null;
  hold_expired?: boolean | null;
};

export type StockStatus = 'IN_STOCK' | 'LOW_STOCK' | 'SUBSTITUTION' | 'UNAVAILABLE';

export type ReservationStatus = 'HELD' | 'CONFLICT' | 'SUBSTITUTED' | 'PENDING';

export type ReservationLineItem = {
  gearItemId: string;
  name: string;
  /** Current RMS category (product_group.name) this line matched against —
   *  undefined if the line never matched anything. Used only to decide
   *  whether the substitute-candidates dropdown has anything to show;
   *  never sent to Current RMS. */
  category?: string;
  quantity: number;
  unitPrice: number;
  reservationStatus: ReservationStatus;
  substituteAvailable: boolean;
  substituteName?: string;
  substituteUnitPrice?: number;
  expiresAt: string;
  rmsHoldId?: string;
};

/** One live, in-stock Current RMS product offered as a substitute choice in
 *  the operator's dropdown — see inventory-service's
 *  GET /reservations/{id}/lines/{lineId}/substitute-candidates. Read-only;
 *  never written back to Current RMS. */
export type SubstituteCandidate = {
  id: string;
  name: string;
  dayRate: number | null;
  quantityAvailable: number;
};

export type StockReservation = {
  id: string;
  bookingId: string;
  lines: ReservationLineItem[];
  reservedAt: string;
  expiresAt: string;
  allHeld: boolean;
  requiresSubstitution: boolean;
  /** Generic link to the account's Current RMS web app, for the operator to
   *  check availability and place a hold manually — never a per-item deep
   *  link (GLOW has no reliable mapping to Current RMS's internal product
   *  ids). Undefined if inventory-service has no subdomain configured. */
  currentRmsWebUrl?: string;
};

export type GearItem = {
  id: string;
  name: string;
  quantity: number;
  unitPrice: number;
  stockStatus: StockStatus;
  substitutionNote?: string;
};

export type BookingJob = {
  id: string;
  clientName: string;
  clientEmail: string;
  receivedAt: string;
  subject: string;
  emailBody: string;
  status: WorkflowStatus;
  /** Raw backend status string — used by the queue to differentiate RECEIVED vs PROCESSING cards. */
  backendStatus?: string;
  /** ISO timestamp from the backend workflow record — used to detect stalled PROCESSING jobs. */
  created_at?: string;
  isThreadReply: boolean;
  threadRootId?: string;
  /** Email of the operator who has this booking claimed (backend
   *  assigned_to_email) — drives the "Claimed by X" badge on InboxQueue
   *  cards past RECEIVED. Undefined/null means unclaimed. */
  assignedToEmail?: string | null;
  gear: GearItem[];
  draftSubject: string;
  draftBody: string;
  draftId?: string | null;
  aiConfidence: number;
  location: string;
  rentalDates: string;
  /** Raw ISO start/end dates behind the `rentalDates` display string — used
   *  when calling reserveStock(), which needs real dates, not the formatted
   *  label. Absent until the draft has been enriched with extracted_dates. */
  rentalDateRange?: { start: string; end: string };
  quoteTotalRands: number;
  /** "CPT" | "CPT Local" | null — operator's confirmed tier choice on a Cape
   *  Town booking. null means the location-based default (or flat-rate
   *  fallback) is still in effect. See [[finding-cpt-jhb-tier-pricing-doc-gap]]. */
  priceTier?: 'CPT' | 'CPT Local' | null;
};
